import { encodeAbiParameters, keccak256, type Address, type Hex } from 'viem'

/**
 * GMX `DataStore` key derivation (mirrors `Keys.sol`).
 *
 * The per-account deposit / withdrawal request sets are stored under
 * `keccak256(abi.encode(<LIST_CONSTANT>, account))`, where each list
 * constant is itself `keccak256(abi.encode("<NAME>"))`. We reproduce that
 * exactly so the reader can enumerate a user's pending request keys via
 * `DataStore.getBytes32ValuesAt`.
 */

const hashString = (s: string): Hex =>
  keccak256(encodeAbiParameters([{ type: 'string' }], [s]))

const ACCOUNT_DEPOSIT_LIST = hashString('ACCOUNT_DEPOSIT_LIST')
const ACCOUNT_WITHDRAWAL_LIST = hashString('ACCOUNT_WITHDRAWAL_LIST')

const accountListKey = (listConstant: Hex, account: Address): Hex =>
  keccak256(
    encodeAbiParameters(
      [{ type: 'bytes32' }, { type: 'address' }],
      [listConstant, account],
    ),
  )

/** Key of the set holding `account`'s pending GM deposit request keys. */
export const accountDepositListKey = (account: Address): Hex =>
  accountListKey(ACCOUNT_DEPOSIT_LIST, account)

/** Key of the set holding `account`'s pending GM withdrawal request keys. */
export const accountWithdrawalListKey = (account: Address): Hex =>
  accountListKey(ACCOUNT_WITHDRAWAL_LIST, account)

/**
 * PnL-factor type used for GM/GLV token-price display (mirrors what the
 * GMX UI passes to `getMarketTokenPrice`).
 */
export const MAX_PNL_FACTOR_FOR_TRADERS = hashString(
  'MAX_PNL_FACTOR_FOR_TRADERS',
)

/**
 * Plain `DataStore.getUint` config keys for the execution-fee estimate.
 * `feeTokenAmount = (baseGasLimit + estimatedGasLimit * multiplier / 1e30) *
 * gasPrice` — mirrors the GMX SDK's `getExecutionFee`.
 */
export const GMX_GAS_KEYS = {
  estimatedGasFeeBaseAmount: hashString('EXECUTION_GAS_FEE_BASE_AMOUNT_V2_1'),
  estimatedGasFeeMultiplierFactor: hashString(
    'EXECUTION_GAS_FEE_MULTIPLIER_FACTOR',
  ),
  depositGasLimit: hashString('DEPOSIT_GAS_LIMIT'),
  withdrawalGasLimit: hashString('WITHDRAWAL_GAS_LIMIT'),
  glvDepositGasLimit: hashString('GLV_DEPOSIT_GAS_LIMIT'),
  glvWithdrawalGasLimit: hashString('GLV_WITHDRAWAL_GAS_LIMIT'),
  glvPerMarketGasLimit: hashString('GLV_PER_MARKET_GAS_LIMIT'),
} as const

/**
 * Deposit-cap key bases (mirror `Keys.sol`). GMX rejects a deposit once a
 * market's pool would exceed either an absolute token cap (`MAX_POOL_AMOUNT`)
 * or a USD cap (`MAX_POOL_USD_FOR_DEPOSIT`); GLV additionally caps the GM
 * balance it may hold per market. These bound how much new liquidity a market
 * can still accept — the deposit-side counterpart to TVL.
 */
const MAX_POOL_AMOUNT = hashString('MAX_POOL_AMOUNT')
const MAX_POOL_USD_FOR_DEPOSIT = hashString('MAX_POOL_USD_FOR_DEPOSIT')
const GLV_MAX_MARKET_TOKEN_BALANCE_USD = hashString(
  'GLV_MAX_MARKET_TOKEN_BALANCE_USD',
)
const GLV_MAX_MARKET_TOKEN_BALANCE_AMOUNT = hashString(
  'GLV_MAX_MARKET_TOKEN_BALANCE_AMOUNT',
)

const marketTokenKey = (base: Hex, market: Address, token: Address): Hex =>
  keccak256(
    encodeAbiParameters(
      [{ type: 'bytes32' }, { type: 'address' }, { type: 'address' }],
      [base, market, token],
    ),
  )

/** Cap on a market's pool token amount (native units). `Keys.maxPoolAmountKey`. */
export const maxPoolAmountKey = (market: Address, token: Address): Hex =>
  marketTokenKey(MAX_POOL_AMOUNT, market, token)

/** Cap on a market's pool value (USD, 1e30) for deposits.
 *  `Keys.maxPoolUsdForDepositKey`. */
export const maxPoolUsdForDepositKey = (market: Address, token: Address): Hex =>
  marketTokenKey(MAX_POOL_USD_FOR_DEPOSIT, market, token)

/** Cap on the USD value of GM tokens a GLV may hold for one market.
 *  `Keys.glvMaxMarketTokenBalanceUsdKey` — keyed by (glv, market). */
export const glvMaxMarketTokenBalanceUsdKey = (
  glv: Address,
  market: Address,
): Hex => marketTokenKey(GLV_MAX_MARKET_TOKEN_BALANCE_USD, glv, market)

/** Cap on the GM token amount a GLV may hold for one market (native units).
 *  `Keys.glvMaxMarketTokenBalanceAmountKey` — keyed by (glv, market). */
export const glvMaxMarketTokenBalanceAmountKey = (
  glv: Address,
  market: Address,
): Hex => marketTokenKey(GLV_MAX_MARKET_TOKEN_BALANCE_AMOUNT, glv, market)

/**
 * Pool-reserve key bases (mirror `Keys.sol`). Open interest + reserve factor
 * bound how much liquidity an LP can actually withdraw: pool tokens reserved
 * to back open trader positions cannot leave the pool. The withdrawal-side
 * counterpart to the deposit caps above.
 */
const OPEN_INTEREST = hashString('OPEN_INTEREST')
const OPEN_INTEREST_IN_TOKENS = hashString('OPEN_INTEREST_IN_TOKENS')
const RESERVE_FACTOR = hashString('RESERVE_FACTOR')

const marketCollateralKey = (
  base: Hex,
  market: Address,
  collateralToken: Address,
  isLong: boolean,
): Hex =>
  keccak256(
    encodeAbiParameters(
      [
        { type: 'bytes32' },
        { type: 'address' },
        { type: 'address' },
        { type: 'bool' },
      ],
      [base, market, collateralToken, isLong],
    ),
  )

/** Market open interest (USD, 1e30) on one side, for positions collateralised
 *  in `collateralToken`. Mirrors `Keys.openInterestKey`. */
export const openInterestKey = (
  market: Address,
  collateralToken: Address,
  isLong: boolean,
): Hex => marketCollateralKey(OPEN_INTEREST, market, collateralToken, isLong)

/** Market open interest in index tokens (native units) on one side, for
 *  positions collateralised in `collateralToken`. `Keys.openInterestInTokensKey`. */
export const openInterestInTokensKey = (
  market: Address,
  collateralToken: Address,
  isLong: boolean,
): Hex =>
  marketCollateralKey(OPEN_INTEREST_IN_TOKENS, market, collateralToken, isLong)

/** Market reserve factor (1e30) on one side — the fraction of the side's pool
 *  value that may be reserved for open positions. `Keys.reserveFactorKey`. */
export const reserveFactorKey = (market: Address, isLong: boolean): Hex =>
  keccak256(
    encodeAbiParameters(
      [{ type: 'bytes32' }, { type: 'address' }, { type: 'bool' }],
      [RESERVE_FACTOR, market, isLong],
    ),
  )
